<script setup>
import moment from "moment/moment";
import {useRoute} from "vue-router";
import {nextTick, onMounted, ref, watch} from "vue";
import useTreeReportMap from "@/components/tree-reports/composables/useTreeReportMap";
import forestreeHelpers from "@/helpers/forestreeHelpers";
import axios from "axios";
import {preloadImages} from "@/config/ForestreeIconFactory.js";
import {useDefaultsListStore} from "@/stores/defaultsListStore";
import {storeToRefs} from "pinia";
import useMapImagery from "@/components/maps/shared/composables/useMapImagery.js";

const defaultsListStore = useDefaultsListStore()
const {workTasks,defaults} = storeToRefs(defaultsListStore)
const route = useRoute();
const today = ref(moment().format("dddd Do MMM YYYY"));
const loading = ref(false);
const unauthorised = ref(false);
const apikey = ref(false);
const mapCreated = ref(false);
const treeInfo = ref(null);
const allTrees = ref(null);
const photos = ref(null);
const previous_valuations = ref([]);
const valuation = ref(null);
const is_estimated = ref(false);


const {map,osm, imageService, aerialGroup, labels, satellite, aerial,createMap, baseMapSwitch} = useTreeReportMap();


const getIcon = (attr) => {
    if (attr === "m") {
        return "Works";
    } else if (attr === "i") {
        return "Inspection";
    } else if (attr === "w") {
        return "Watering";
    } else if (attr === "s") {
        return "Stump";
    } else if (attr === "p") {
        return "Planting";
    } else {
        return "1";
    }
};
const getTreeInfo = async () => {
    loading.value = true;
    var prefix = "/api/reports/tree";
    var url = null;

    if (route.query.treeId) {
        url = "?treeId=" + route.query.treeId;
    } else if (route.query.treeId) {
        url = "?assetId=" + route.query.treeId;
    } else if (route.query.address) {
        url = "?address=" + route.query.address;
    } else {
        url = null;
        loading.value = false;
    }

    if (url && route.query.key) {
        url = url + "&key=" + route.query.key;
        prefix = "/link/api/reports/tree";
        apikey.value = true;
    }

    if (url) {
        axios
            .get(prefix + url)
            .then((response) => {
                allTrees.value = response.data.allTrees;
                treeInfo.value = response.data.treeInfo;
                photos.value = response.data.photos;
                defaultsListStore.updateStoreFields(response.data.defaults);
                loading.value = false;
                previous_valuations.value = response.data.previous_valuations;
                valuation.value = (response.data.valuation.value / 100).toFixed(2);
                is_estimated.value = response.data.isEstimated;

                setTimeout(() => {
                    createMap(response.data.treeInfo);
                }, 1000);
            })
            .catch((error) => {
                console.log(error.message)
                if (error.response?.status === 401) {
                    unauthorised.value = true;
                }
                loading.value = false;
            });
    }
};
watch(
    () => route.query.treeId,
    (newId) => {
        if (newId) {
            getTreeInfo();
        }
    }
);

//region Risk and Traq risk

const getTraqFailureText = (failure) => {
    if (failure === 1) {
        return "Improbable";
    } else if (failure === 2) {
        return "Possible";
    } else if (failure === 3) {
        return "Probable";
    } else if (failure === 4) {
        return "Imminent";
    } else {
        return "Unknown";
    }
}
const getTraqTargetText = (target) => {
    if (target === 1) {
        return "Very Low";
    } else if (target === 2) {
        return "Low";
    } else if (target === 3) {
        return "Medium";
    } else if (target === 4) {
        return "High";
    } else {
        return "Unknown";
    }

}
const getTraqConsText = (cons) => {
    if (cons === 1) {
        return "Negligible";
    } else if (cons === 2) {
        return "Minor";
    } else if (cons === 3) {
        return "Significant";
    } else if (cons === 4) {
        return "Severe";
    } else {
        return "Unknown";
    }
}
const getTraqRiskText = (risk) => {
    if (risk === 1) {
        return "Low";
    } else if (risk === 2) {
        return "Medium";
    } else if (risk === 3) {
        return "High";
    } else if (risk === 4) {
        return "Extreme";
    } else {
        return "Unknown";
    }
}
const getRiskColour = (risk) => {
    if (risk === 1) {
        return "bg-green-200 text-green-800";
    } else if (risk === 2) {
        return "bg-orange-200 text-orange-800";
    } else if (risk === 3) {
        return "bg-red-200 text-red-800";
    } else if (risk === 4) {
        return "bg-red-300 text-red-900";
    } else {
        return "bg-slate-100 text-slate-800";
    }
}
const getOverallRiskColour = (risk) => {
    if (risk === 1) {
        return "bg-green-600 text-green-100";
    } else if (risk === 2) {
        return "bg-orange-600 text-orange-100";
    } else if (risk === 3) {
        return "bg-red-600 text-red-100";
    } else if (risk === 4) {
        return "bg-red-800 text-red-100";
    } else {
        return "bg-slate-100 text-slate-800";
    }
}
//endregion
</script>

<template>
    <div class="w-full mx-auto lg:max-w-6xl">
        <div v-if="unauthorised" class="p-4 border-l-4 border-yellow-400 bg-yellow-50">
            <div class="flex">
                <div class="flex-shrink-0">
                    <svg class="w-5 h-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path
                            fill-rule="evenodd"
                            d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </div>
                <div class="ml-3">
                    <p class="text-sm text-yellow-700">Unauthorised access.</p>
                </div>
            </div>
        </div>

        <div v-if="treeInfo" class="mt-4 overflow-hidden bg-white shadow sm:rounded-lg">
            <div v-if="treeInfo" class="px-4 py-5 border-b border-slate-200 sm:px-6">
                <h3 class="pb-2 text-2xl font-medium leading-6 text-green-600 uppercase">Tree Report</h3>
                <p class="text-lg font-medium text-slate-600">
                    {{ treeInfo.species.common_name }}
                </p>
                <p class="leading-5 text-slate-500">
                    {{ treeInfo.species.genus }} <i>{{ treeInfo.species.species }}</i> {{ treeInfo.species.variety }}
                </p>
            </div>
            <div class="w-full pb-6 lg:flex">
                <div v-if="treeInfo" class="w-full px-4 py-5 sm:px-6 lg:w-1/2">
                    <span
                        v-if="treeInfo.trees_status_id === 1"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium leading-5 bg-blue-200 text-blue-800 mb-3"
                    >
                        Planting Location
                    </span>

                    <span
                        v-if="treeInfo.trees_status_id === 2"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium leading-5 bg-green-200 text-green-800 mb-3"
                    >
                        Active
                    </span>

                    <span
                        v-if="treeInfo.trees_status_id === 3"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium leading-5 bg-slate-100 text-slate-800 mb-3"
                    >
                        Stump
                    </span>

                    <span
                        v-if="treeInfo.trees_status_id === 4"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium leading-5 bg-red-200 text-red-800 mb-3"
                    >
                        Removed Tree
                    </span>

                    <span
                        v-if="treeInfo.park_tree"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium leading-5 bg-green-200 text-green-800 mb-3"
                    >
                        Park Tree
                    </span>

                    <span
                        v-if="treeInfo.sig_tree_id > 0"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium leading-5 bg-yellow-200 text-yellow-800 mb-3"
                    >
                        Significant
                    </span>

                    <span
                        v-if="treeInfo.private_tree"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium leading-5 bg-slate-100 text-slate-800 mb-3"
                    >
                        Private Tree
                    </span>

                    <div v-if="treeInfo.spatial_tags && treeInfo.spatial_tags.length" class="w-full pb-1">
                        <span
                            v-for="tag in treeInfo.spatial_tags"
                            class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium leading-5 bg-slate-600 text-slate-100 mr-1 mb-3 whitespace-nowrap"
                        >
                            {{ tag.name }}
                        </span>
                    </div>

                    
                    <div v-if="treeInfo.tags && treeInfo.tags.length" class="w-full pb-1">
                        <span
                            v-for="tag in treeInfo.tags"
                            class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium leading-5 bg-blue-600 text-blue-100 mr-1 mb-3 whitespace-nowrap"
                        >
                            {{ tag.name }}
                        </span>
                    </div>

                    
                    <div v-if="treeInfo.pest_diseases && treeInfo.pest_diseases.length" class="w-full pb-1">
                        <span
                            v-for="pd in treeInfo.pest_diseases"
                            class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium leading-5 bg-orange-600 text-blue-100 mr-1 mb-3 whitespace-nowrap"
                        >
                            {{ pd.name }}
                        </span>
                    </div>





                    <dl class="grid grid-cols-1 grid-cols-2 pt-2 gap-x-4 gap-y-8">
                        <div v-show="treeInfo.approx_address || treeInfo.address" class="col-span-2">
                            <dt class="text-sm leading-5 text-slate-400">Approximate Address</dt>
                            <dd class="text-lg font-semibold leading-5 text-slate-700">
                                {{ treeInfo.address ? treeInfo.address : treeInfo.approx_address }}
                            </dd>
                        </div>
                        <div class="col-span-1" :class="treeInfo.approx_address || treeInfo.address ? '' : 'pt-3'">
                            <dt class="text-sm leading-5 text-slate-400">Tree ID</dt>
                            <dd
                                class="text-lg font-semibold leading-5 text-blue-700 cursor-pointer hover:text-blue-800"
                            >
                                <a :href="'/map?treeId=' + treeInfo.id" target="_blank">{{ treeInfo.id }}</a>
                            </dd>
                        </div>
                        <div class="col-span-1" :class="treeInfo.approx_address || treeInfo.address ? '' : 'pt-3'">
                            <dt class="text-sm leading-5 text-slate-400">Asset ID</dt>
                            <dd class="text-lg font-semibold leading-5 text-slate-700">
                                {{ treeInfo.asset_id }}
                            </dd>
                        </div>
                        <div class="col-span-1">
                            <dt class="text-sm leading-5 text-slate-400">Planted Date</dt>
                            <dd v-if="treeInfo.planted_date" class="text-lg font-semibold leading-5 text-slate-700">
                                {{ forestreeHelpers.dateTime(treeInfo.planted_date, true) }}
                            </dd>
                            <dd v-else class="text-lg font-semibold leading-5 text-slate-700">
                                {{ "-" }}
                            </dd>
                        </div>
                        <div class="col-span-1">
                            <dt class="text-sm leading-5 text-slate-400">Current Risk</dt>
                            <dd class="text-lg font-semibold leading-5 text-slate-700">
                                <span
                                    v-if="treeInfo.trees_risk_id === 1"
                                    class="inline-flex items-center px-3 py-1 leading-5 text-green-800 bg-green-100 rounded-md"
                                >
                                    Low
                                </span>
                                <span
                                    v-if="treeInfo.trees_risk_id === 2"
                                    class="inline-flex items-center px-3 py-1 leading-5 text-orange-800 bg-orange-100 rounded-md"
                                >
                                    Moderate
                                </span>
                                <span
                                    v-if="treeInfo.trees_risk_id === 3"
                                    class="inline-flex items-center px-3 py-1 leading-5 text-red-800 bg-red-100 rounded-md"
                                >
                                    High
                                </span>
                                <span
                                    v-if="treeInfo.trees_risk_id === 4"
                                    class="inline-flex items-center px-3 py-1 leading-5 text-red-100 bg-red-600 rounded-md"
                                >
                                    Extreme
                                </span>
                                <span
                                    v-if="!treeInfo.trees_risk_id"
                                    class="inline-flex items-center px-3 py-1 leading-5 rounded-md bg-slate-100 text-slate-800"
                                >
                                    TBD
                                </span>
                            </dd>
                        </div>
                        <div class="col-span-1">
                            <dt class="text-sm leading-5 text-slate-400">Age</dt>
                            <dd class="text-lg font-semibold leading-5 text-slate-700">
                                {{ treeInfo.age ? treeInfo.age.name : "-" }}
                            </dd>
                        </div>
                        <div class="col-span-1">
                            <dt class="text-sm leading-5 text-slate-400">ULE</dt>
                            <dd class="text-lg font-semibold leading-5 text-slate-700">
                                {{ treeInfo.ule ? treeInfo.ule.name : "-" }}
                            </dd>
                        </div>
                        <div class="col-span-1">
                            <dt class="text-sm leading-5 text-slate-400">Health</dt>
                            <dd class="text-lg font-semibold leading-5 text-slate-700">
                                {{ treeInfo.health ? treeInfo.health.name : "-" }}
                            </dd>
                        </div>
                        <div class="col-span-1">
                            <dt class="text-sm leading-5 text-slate-400">Structure</dt>
                            <dd class="text-lg font-semibold leading-5 text-slate-700">
                                {{ treeInfo.structure ? treeInfo.structure.name : "-" }}
                            </dd>
                        </div>

                        <div class="col-span-1">
                            <dt class="text-sm leading-5 text-slate-400">Height (m)</dt>
                            <dd class="text-lg font-semibold leading-5 text-slate-700">
                                {{ treeInfo.height ? treeInfo.height : "-" }}
                            </dd>
                        </div>

                        <div class="col-span-1">
                            <dt class="text-sm leading-5 text-slate-400">Height Range</dt>
                            <dd class="text-lg font-semibold leading-5 text-slate-700">
                                {{ treeInfo.hght ? treeInfo.hght.name : "-" }}
                            </dd>
                        </div>

                        <div class="col-span-1">
                            <dt class="text-sm leading-5 text-slate-400">Canopy Size (m)</dt>
                            <dd class="text-lg font-semibold leading-5 text-slate-700">
                                {{ treeInfo.canopy ? treeInfo.canopy : "-" }}
                            </dd>
                        </div>

                        <div class="col-span-1">
                            <dt class="text-sm leading-5 text-slate-400">Canopy Size</dt>
                            <dd class="text-lg font-semibold leading-5 text-slate-700">
                                {{ treeInfo.cnpy ? treeInfo.cnpy.name : "-" }}
                            </dd>
                        </div>
                        <div class="col-span-1">
                            <dt class="text-sm leading-5 text-slate-400">Canopy N-S (m)</dt>
                            <dd class="text-lg font-semibold leading-5 text-slate-700">
                                {{ treeInfo.canopy_ns ? treeInfo.canopy_ns : "-" }}
                            </dd>
                        </div>
                        <div class="col-span-1">
                            <dt class="text-sm leading-5 text-slate-400">Canopy E-W (m)</dt>
                            <dd class="text-lg font-semibold leading-5 text-slate-700">
                                {{ treeInfo.canopy_ew ? treeInfo.canopy_ew : "-" }}
                            </dd>
                        </div>
                        <div class="col-span-1">
                            <dt class="text-sm leading-5 text-slate-400">Circumference</dt>
                            <dd class="text-lg font-semibold leading-5 text-slate-700">
                                {{ treeInfo.circumference ? treeInfo.circumference.name : "-" }}
                            </dd>
                        </div>
                        <div class="col-span-1">
                            <dt class="text-sm leading-5 text-slate-400">Verge Size</dt>
                            <dd class="text-lg font-semibold leading-5 text-slate-700">
                                {{ treeInfo.verge ? treeInfo.verge.name : "-" }}
                            </dd>
                        </div>
                        <div class="col-span-1">
                            <dt class="text-sm leading-5 text-slate-400">DBH (cm)</dt>
                            <dd class="text-lg font-semibold leading-5 text-slate-700">
                                {{ parseFloat(treeInfo.dbh) > 0 ? treeInfo.dbh : "-" }}
                            </dd>
                        </div>
                        <div class="col-span-1">
                            <dt class="text-sm leading-5 text-slate-400">DBH Range</dt>
                            <dd class="text-lg font-semibold leading-5 text-slate-700">
                                {{ treeInfo.dbh_val ? treeInfo.dbh_val.name : "-" }}
                            </dd>
                        </div>
                    </dl>
                </div>
                <div class="w-full px-4 py-5 sm:px-6 lg:w-1/2">
                    <div class="flex justify-end w-full">
                        <button
                            @click="baseMapSwitch()"
                            type="button"
                            class="inline-flex items-center px-3 py-2 mb-2 text-sm font-medium leading-4 border border-transparent rounded-sm shadow-sm text-slate-500 bg-slate-100 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
                        >
                            <svg
                                class="w-4 h-4 mr-1 text-cool-slate-400"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                ></path>
                            </svg>
                            Toggle Basemap
                        </button>
                    </div>

                    <div id="minimap" class="w-full" style="height: 450px"></div>

                    <div v-if="treeInfo" class="flex justify-between w-full mb-3">
                        <div class="pt-1 pb-2 text-sm text-center text-slate-400">
                            {{ treeInfo.lat }}, {{ treeInfo.lng }}
                        </div>
                        <div class="pt-1 pb-2 text-sm text-center">
                            <a
                                :href="
                                    'http://maps.google.com/maps?q=&layer=c&cbll=' + treeInfo.lat + ',' + treeInfo.lng
                                "
                                target="_blank"
                                class="text-blue-600"
                            >
                                Open in Google Street View
                            </a>
                        </div>
                    </div>

                    <div v-if="treeInfo && treeInfo.tpz" class="w-full px-4 py-5 bg-orange-700 sm:px-6">
                        <dl class="grid grid-cols-2 gap-x-4 gap-y-8">
                            <div class="col-span-1">
                                <dt class="text-sm leading-5 text-white">
                                    TPZ {{ parseInt(treeInfo.dbh) > 0 ? "" : "(Estimate)" }}
                                </dt>
                                <dd class="text-xl font-semibold leading-5 text-white">
                                    {{ treeInfo.tpz.toFixed(2) }}m
                                </dd>
                            </div>
                            <div class="col-span-1">
                                <dt class="text-sm leading-5 text-white">
                                    SRZ {{ parseInt(treeInfo.dbh) > 0 ? "" : "(Estimate)" }}
                                </dt>
                                <dd class="text-xl font-semibold leading-5 text-white">
                                    {{ treeInfo.srz.toFixed(2) }}m
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </div>

            <!-- inspection history -->
            <div class="py-6">
                <div class="px-4">
                    <div class="sm:flex sm:items-center">
                        <div class="sm:flex-auto">
                            <h1 class="text-xl font-semibold text-slate-900">Inspection/Observation History</h1>
                            <p class="mt-2 text-sm text-slate-700">
                                A history of Inspections/Observations against this tree.
                            </p>
                        </div>
                    </div>
                    <div v-if="treeInfo && treeInfo.next_inspect_date" class="p-4 mt-4 rounded-md bg-blue-50">
                        <div class="flex">
                            <div class="flex-shrink-0">
                                <!-- Heroicon name: solid/information-circle -->
                                <svg
                                    class="w-5 h-5 text-blue-400"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </div>
                            <div class="flex-1 ml-3 md:flex md:justify-between">
                                <p class="text-sm text-blue-700">
                                    Next Inspection Due
                                    <b>{{ forestreeHelpers.justDate(treeInfo.next_inspect_date) }}</b>
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="flex flex-col pt-4 pb-8">
                        <div class="-mx-4 -my-2 overflow-x-auto ">
                            <div class="inline-block min-w-full py-2 align-middle">
                                <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5">
                                    <table class="min-w-full divide-y divide-slate-400">
                                        <thead class="bg-slate-100">
                                        <tr>
                                            <th scope="col"
                                                class="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-slate-900 sm:pl-6">
                                                Date
                                            </th>
                                            <th scope="col"
                                                class="whitespace-pre-wrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                                style="min-width:200px">Notes
                                            </th>
                                            <th scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900">
                                                Work Req.
                                            </th>
                                            <th scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900">
                                                Work Priority
                                            </th>
                                            <th scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900">
                                                Work Tasks
                                            </th>
                                            <th scope="col"
                                                class="whitespace-pre-wrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                                style="min-width:200px">Work Notes
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody class="bg-white divide-y divide-slate-100"
                                               v-if="treeInfo && treeInfo.trees_inspection.length > 0"
                                               v-for="i in treeInfo.trees_inspection" :key="i.id">
                                        <tr class="bg-slate-50">
                                            <td class="py-2 pl-4 pr-3 text-sm whitespace-nowrap text-slate-500">
                                                <div class="font-semibold"> {{
                                                        forestreeHelpers.dateTime(i.created_at, true)
                                                    }}
                                                </div>
                                                <div class="font-semibold text-slate-700">
                                                    {{ i.completed_by ? i.completed_by.name : 'Data Import' }}
                                                </div>
                                            </td>
                                            <td class="px-2 py-2 text-xs whitespace-pre-wrap text-slate-500"
                                                style="min-width:200px">{{ i.notes }}
                                            </td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-500">
                                                {{ i.work_req ? 'Yes' : 'No' }}
                                            </td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-500">
                                    <span v-if="i.priority === 1"
                                          class="inline-flex items-center px-3 py-0.5 rounded-md text-sm leading-5 bg-green-100 text-green-800">
                                      Low
                                    </span>
                                                <span v-if="i.priority === 2"
                                                      class="inline-flex items-center px-3 py-0.5 rounded-md text-sm leading-5 bg-orange-100 text-orange-800">
                                      Medium
                                    </span>
                                                <span v-if="i.priority === 3"
                                                      class="inline-flex items-center px-3 py-0.5 rounded-md text-sm leading-5 bg-red-100 text-red-800">
                                      High
                                    </span>
                                            </td>
                                            <td class="px-2 py-2 text-sm text-slate-500">
                            <span v-for="(task, tidx) in i.works_as_list" :key="tidx"
                                  class="whitespace-nowrap mb-0.5 inline-block bg-slate-600 rounded-full px-3 text-sm text-slate-100 hover:text-slate-300 mr-1"
                            >
                                    {{ forestreeHelpers.getWorkTask(task, workTasks) }}
                            </span>
                                            </td>
                                            <td class="px-2 py-2 text-xs whitespace-pre-wrap text-slate-500"
                                                style="min-width:200px">{{ i.work_notes }}
                                            </td>
                                        </tr>
                                        <!-- New Row for Risk Assessments -->
                                        <tr v-if="i.risk_assessments && i.risk_assessments.length > 0"
                                            v-for="(r, index) in i.risk_assessments" :key="r.id"
                                            class="text-sm border-t text-slate-500 border-slate-200">

                                            <td colspan="1"
                                                class="py-2 pb-4 pl-4 text-xs font-semibold align-bottom whitespace-nowrap">
                                                Risk Assessment {{ index + 1 }}
                                            </td>
                                            <td colspan="1" class="py-2">
                                                <div>
                                                    <nav v-if="index === 0"
                                                         class="inline-flex -space-x-px rounded shadow-sm isolate"
                                                         aria-label="Risk Assessment">
                                                        <div title="Likelihood of failure"
                                                             class="relative inline-flex items-center justify-center w-20 py-2 text-xs font-semibold rounded-l-md whitespace-nowrap ring-1 ring-inset ring-white focus:z-20 focus:outline-offset-0">
                                                            Failure
                                                        </div>
                                                        <div title="Likelihood of Impacting Target"
                                                             class="relative inline-flex items-center justify-center w-20 py-2 text-xs font-semibold whitespace-nowrap ring-1 ring-inset ring-white focus:z-20 focus:outline-offset-0">
                                                            Impact
                                                        </div>
                                                        <div title="Consequences of Failure"
                                                             class="relative inline-flex items-center justify-center w-20 py-2 text-xs font-semibold whitespace-nowrap ring-1 ring-inset ring-white focus:z-20 focus:outline-offset-0">
                                                            Consequence
                                                        </div>
                                                        <div title="Overall Risk"
                                                             class="relative inline-flex items-center justify-center w-20 py-2 text-xs font-semibold rounded-r-md whitespace-nowrap ring-1 ring-inset ring-white focus:z-20 focus:outline-offset-0">
                                                            Risk
                                                        </div>
                                                    </nav>
                                                    <nav class="inline-flex -space-x-px rounded shadow-sm isolate"
                                                         aria-label="Risk Assessment">
                                                        <div title="Likelihood of failure"
                                                             :class="getRiskColour(r.traq_failure)"
                                                             class="relative inline-flex items-center justify-center w-20 py-2 text-xs rounded-l-md whitespace-nowrap ring-1 ring-inset ring-white focus:z-20 focus:outline-offset-0">
                                                            {{ getTraqFailureText(r.traq_failure) }}
                                                        </div>
                                                        <div title="Likelihood of Impacting Target"
                                                             :class="getRiskColour(r.traq_target)"
                                                             class="relative inline-flex items-center justify-center w-20 py-2 text-xs whitespace-nowrap ring-1 ring-inset ring-white focus:z-20 focus:outline-offset-0">
                                                            {{ getTraqTargetText(r.traq_target) }}
                                                        </div>
                                                        <div title="Consequences of Failure"
                                                             :class="getRiskColour(r.traq_consequence)"
                                                             class="relative inline-flex items-center justify-center w-20 py-2 text-xs whitespace-nowrap ring-1 ring-inset ring-white focus:z-20 focus:outline-offset-0">
                                                            {{ getTraqConsText(r.traq_consequence) }}
                                                        </div>
                                                        <div title="Overall Risk" :class="getOverallRiskColour(r.risk)"
                                                             class="relative inline-flex items-center justify-center w-20 py-2 text-xs font-semibold rounded-r-md whitespace-nowrap ring-1 ring-inset ring-white focus:z-20 focus:outline-offset-0">
                                                            {{ getTraqRiskText(r.risk) }}
                                                        </div>
                                                    </nav>
                                                </div>
                                            </td>
                                            <td colspan="2" class="py-2 pb-4 text-xs align-bottom whitespace-nowrap">
                                                <div v-if="index === 0" class="pb-4 font-semibold">Tree Part</div>
                                                {{ r.tree_part ? r.tree_part.name : 'NA' }}
                                            </td>
                                            <td colspan="2" class="py-2 pb-4 text-xs align-bottom whitespace-nowrap">
                                                <div v-if="index === 0" class="pb-4 font-semibold">Target</div>
                                                {{ r.targets ? r.targets : 'NA' }}
                                            </td>
                                        </tr>
                                        </tbody>
                                        <tbody class="italic bg-white divide-y divide-slate-200 text-slate-400" v-else>
                                        <tr class="px-2 py-2 sm:pl-6">No Inspection/Observation History</tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <!-- attribute history -->
            <div class="py-6">
                <div class="px-4">
                    <div class="sm:flex sm:items-center">
                        <div class="sm:flex-auto">
                            <h1 class="text-xl font-semibold text-slate-900">Attribute History</h1>
                            <p class="mt-2 text-sm text-slate-700">A history of Tree Attribute changes to this tree.</p>
                        </div>
                    </div>
                    <div class="flex flex-col py-8">
                        <div class="-mx-4 -my-2 overflow-x-auto">
                            <div class="inline-block min-w-full py-2 align-middle">
                                <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5">
                                    <table class="min-w-full divide-y divide-slate-300">
                                        <thead class="bg-slate-50">
                                        <tr>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-slate-900 sm:pl-6"
                                            >
                                                Date
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                User
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Age
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Height
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Height (m)
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Health
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Structure
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Canopy
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Canopy (m)
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Canopy N-S (m)
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Canopy E-W (m)
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Circumference
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                ULE
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                DBH (cm)
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                DBH Range
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody
                                            class="bg-white divide-y divide-slate-200"
                                            v-if="treeInfo && treeInfo.trees_inspection.length > 0"
                                        >
                                        <tr v-for="i in treeInfo.trees_inspection" :key="i.id">
                                            <td
                                                class="py-2 pl-4 pr-3 text-sm whitespace-nowrap text-slate-500 sm:pl-6"
                                            >
                                                {{ forestreeHelpers.dateTime(i.created_at, true) }}
                                            </td>
                                            <td
                                                class="px-2 py-2 text-sm font-medium whitespace-nowrap text-slate-900"
                                            >
                                                {{ i.completed_by ? i.completed_by.name : "Data Import" }}
                                            </td>

                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-900">
                                                {{ i.age ? i.age.name : "" }}
                                            </td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-900">
                                                {{ i.hght ? i.hght.name : "" }}
                                            </td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-900">
                                                {{ parseInt(i.height) > 0 ? i.height : "" }}
                                            </td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-900">
                                                {{ i.health ? i.health.name : "" }}
                                            </td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-900">
                                                {{ i.structure ? i.structure.name : "" }}
                                            </td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-900">
                                                {{ i.cnpy ? i.cnpy.name : "" }}
                                            </td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-900">
                                                {{ parseInt(i.canopy) > 0 ? i.canopy : "" }}
                                            </td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-900">
                                                {{ parseInt(i.canopy_ns) > 0 ? i.canopy_ns : "" }}
                                            </td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-900">
                                                {{ parseInt(i.canopy_ew) > 0 ? i.canopy_ew : "" }}
                                            </td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-900">
                                                {{ i.circumference ? i.circumference.name : "" }}
                                            </td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-900">
                                                {{ i.ule ? i.ule.name : "" }}
                                            </td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-900">
                                                {{ parseFloat(i.dbh) > 0 ? i.dbh : "" }}
                                            </td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-900">
                                                {{ i.dbh_val ? i.dbh_val.name : "" }}
                                            </td>
                                        </tr>
                                        </tbody>
                                        <tbody class="italic bg-white divide-y divide-slate-200 text-slate-400" v-else>
                                        <tr>
                                            <td>
                                                <p class="px-2 py-2 sm:pl-6">No Works History</p>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- valuation history -->
            <div class="py-6" v-if="defaults && defaults.valuations">
                <div class="px-4">
                    <div class="sm:flex sm:items-center">
                        <div class="sm:flex-auto">
                            <h1 class="text-xl font-semibold text-slate-900">Valuation History</h1>
                            <p class="mt-2 text-sm text-slate-700">A history of Tree Valuations against this tree.</p>
                        </div>
                    </div>
                    <div class="flex flex-col py-8">
                        <div class="-mx-4 -my-2 overflow-x-auto">
                            <div class="inline-block min-w-full py-2 align-middle">
                                <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5">
                                    <table class="min-w-full divide-y divide-slate-300">
                                        <thead class="bg-slate-50">
                                        <tr>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-slate-900 sm:pl-6"
                                            >
                                                Date
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Value
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Type
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Base Value
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody
                                            class="bg-white divide-y divide-slate-200"
                                            v-if="previous_valuations && previous_valuations.length > 0"
                                        >
                                        <tr v-for="i in previous_valuations" :key="i.id">
                                            <td
                                                class="py-2 pl-4 pr-3 text-sm whitespace-nowrap text-slate-500 sm:pl-6"
                                            >
                                                {{ forestreeHelpers.dateTime(i.valuation_date, true) }}
                                            </td>
                                            <td
                                                class="px-2 py-2 text-sm font-medium whitespace-nowrap text-slate-900"
                                            >
                                                ${{
                                                    parseFloat(i.val / 100).toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                    })
                                                }}
                                            </td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-900">
                                                {{
                                                    i.estimated
                                                        ? "Estimated"
                                                        : i.val_imported
                                                            ? "Imported"
                                                            : "Actual"
                                                }}
                                            </td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-900">
                                                ${{
                                                    parseFloat(i.base_value / 100).toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                    })
                                                }}
                                            </td>
                                        </tr>
                                        </tbody>
                                        <tbody class="italic bg-white divide-y divide-slate-200 text-slate-400" v-else>
                                        <tr>
                                            <td>
                                                <p class="px-2 py-2 sm:pl-6">No Valuation History</p>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div
                                    v-if="previous_valuations && previous_valuations.length > 0"
                                    class="float-right px-3 py-2 text-xs font-thin text-slate-600"
                                >
                                    'Estimated' Type indicates the DBH Range was used instead of an exact DBH Value.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- works history -->
            <div class="py-6">
                <div class="px-4">
                    <div class="sm:flex sm:items-center">
                        <div class="sm:flex-auto">
                            <h1 class="text-xl font-semibold text-slate-900">Works History</h1>
                            <p class="mt-2 text-sm text-slate-700">A history of Works against this tree.</p>
                        </div>
                    </div>
                    <div class="flex flex-col py-8">
                        <div class="-mx-4 -my-2 overflow-x-auto">
                            <div class="inline-block min-w-full py-2 align-middle">
                                <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5">
                                    <table class="min-w-full divide-y divide-slate-300">
                                        <thead class="bg-slate-50">
                                        <tr>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-slate-900 sm:pl-6"
                                            >
                                                Date
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Completed By
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Status
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Source
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Work Identified
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Work Completed
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-pre-wrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                                style="min-width: 200px"
                                            >
                                                Notes
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody
                                            class="bg-white divide-y divide-slate-200"
                                            v-if="treeInfo && treeInfo.trees_maintenance.length > 0"
                                        >
                                        <tr v-for="i in treeInfo.trees_maintenance" :key="i.id">
                                            <td
                                                class="py-2 pl-4 pr-3 text-sm whitespace-nowrap text-slate-500 sm:pl-6"
                                            >
                                                {{ forestreeHelpers.dateTime(i.updated_at, true) }}
                                            </td>
                                            <td
                                                v-if="i.status === 'Complete'"
                                                class="px-2 py-2 text-sm font-medium whitespace-nowrap text-slate-900"
                                            >
                                                {{ i.completed_by ? i.completed_by.name : "Data Import" }}
                                            </td>
                                            <td
                                                v-else
                                                class="px-2 py-2 text-sm font-medium whitespace-nowrap text-slate-900"
                                            ></td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-900">
                                                    <span
                                                        v-if="i.status === 'Complete'"
                                                        class="inline-flex items-center px-3 py-0.5 rounded-md text-sm leading-5 bg-green-100 text-green-800"
                                                    >
                                                        {{ i.status }}
                                                    </span>
                                                <span
                                                    v-else
                                                    class="inline-flex items-center px-3 py-0.5 rounded-md text-sm leading-5 bg-red-100 text-red-800"
                                                >
                                                        {{ i.status }}
                                                    </span>
                                            </td>
                                            <td
                                                v-if="i.inspection_link"
                                                class="px-2 py-2 text-sm font-medium whitespace-nowrap text-slate-900"
                                            >
                                                Inspection
                                            </td>
                                            <td
                                                v-else
                                                class="px-2 py-2 text-sm font-medium whitespace-nowrap text-slate-900"
                                            >
                                                Proactive
                                            </td>

                                            <td v-if="i.inspection_link" class="px-2 py-2 text-sm text-slate-500">
                                                    <span
                                                        v-for="(task, tidx) in i.inspection_link.works_as_list"
                                                        :key="tidx"
                                                        class="whitespace-nowrap inline-block bg-slate-600 mb-0.5 rounded-full px-3 text-sm text-slate-100 hover:text-slate-300 mr-1"
                                                    >
                                                        {{ forestreeHelpers.getWorkTask(task, workTasks) }}
                                                    </span>
                                            </td>
                                            <td
                                                v-else
                                                class="px-2 py-2 text-sm font-medium whitespace-nowrap text-slate-900"
                                            >
                                                -
                                            </td>
                                            <td class="px-2 py-2 text-sm text-slate-500">
                                                    <span
                                                        v-for="(task, tidx) in i.works_as_list"
                                                        :key="tidx"
                                                        class="whitespace-nowrap inline-block bg-green-600 mb-0.5 rounded-full px-3 text-sm text-slate-100 hover:text-slate-200 mr-1"
                                                    >
                                                        {{ forestreeHelpers.getWorkTask(task, workTasks) }}
                                                    </span>
                                            </td>
                                            <td
                                                class="px-2 py-2 text-xs whitespace-pre-wrap text-slate-500"
                                                style="min-width: 200px"
                                            >
                                                {{ i.notes }}
                                            </td>
                                        </tr>
                                        </tbody>
                                        <tbody class="italic bg-white divide-y divide-slate-200 text-slate-400" v-else>
                                        <tr>
                                            <td>
                                                <p class="px-2 py-2 sm:pl-6">No Works History</p>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- watering history -->
            <div class="py-6">
                <div class="px-4">
                    <div class="sm:flex sm:items-center">
                        <div class="sm:flex-auto">
                            <h1 class="text-xl font-semibold text-slate-900">Watering History</h1>
                            <p class="mt-2 text-sm text-slate-700">A history of Watering against this tree.</p>
                        </div>
                    </div>
                    <div class="flex flex-col py-8">
                        <div class="-mx-4 -my-2 overflow-x-auto">
                            <div class="inline-block min-w-full py-2 align-middle">
                                <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5">
                                    <table class="min-w-full divide-y divide-slate-300">
                                        <thead class="bg-slate-50">
                                        <tr>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-slate-900 sm:pl-6"
                                            >
                                                Date
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Watered By
                                            </th>
                                            <th
                                                scope="col"
                                                class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-slate-900"
                                            >
                                                Status
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody
                                            class="bg-white divide-y divide-slate-200"
                                            v-if="treeInfo && treeInfo.trees_watering.length > 0"
                                        >
                                        <tr v-for="i in treeInfo.trees_watering" :key="i.id">
                                            <td
                                                class="py-2 pl-4 pr-3 text-sm whitespace-nowrap text-slate-500 sm:pl-6"
                                            >
                                                {{ forestreeHelpers.dateTime(i.updated_at, true) }}
                                            </td>
                                            <td
                                                class="px-2 py-2 text-sm font-medium whitespace-nowrap text-slate-900"
                                            >
                                                {{ i.completed_by ? i.completed_by.name : "-" }}
                                            </td>
                                            <td class="px-2 py-2 text-sm whitespace-nowrap text-slate-900">
                                                    <span
                                                        v-if="i.status === 'Watered'"
                                                        class="inline-flex items-center px-3 py-0.5 rounded-md text-sm leading-5 bg-blue-100 text-blue-800"
                                                    >
                                                        {{ i.status }}
                                                    </span>
                                                <span
                                                    v-else
                                                    class="inline-flex items-center px-3 py-0.5 rounded-md text-sm leading-5 bg-slate-100 text-slate-800"
                                                >
                                                        {{ i.status }}
                                                    </span>
                                            </td>
                                        </tr>
                                        </tbody>
                                        <tbody class="italic bg-white divide-y divide-slate-200 text-slate-400" v-else>
                                        <tr>
                                            <td>
                                                <p class="px-2 py-2 sm:pl-6">No Watering History</p>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- pdfs -->
            <div class="pt-6 pb-6">
                <div class="px-4">
                    <div class="pb-3 sm:flex sm:items-center">
                        <div class="sm:flex-auto">
                            <h1 class="text-xl font-semibold text-slate-900">Files</h1>
                        </div>
                    </div>

                    <dl
                        v-if="photos && photos.length > 0"
                        class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-3"
                    >
                        <div v-for="p in photos.filter((p) => p.type === 'pdf')" :key="p.id" class="col-span-1">
                            <a class="text-slate-500" :href="'/api/photos/show/' + p.file">{{ p.original_name }}</a>
                        </div>
                    </dl>
                    <p v-else class="px-2 py-2 sm:pl-6">No Files</p>
                </div>
            </div>

            <!-- photos -->

            <div v-if="!apikey" class="pt-6 pb-12">
                <div class="px-4">
                    <div class="pb-8 sm:flex sm:items-center">
                        <div class="sm:flex-auto">
                            <h1 class="text-xl font-semibold text-slate-900">Photos</h1>
                        </div>
                    </div>

                    <dl
                        v-if="photos && photos.length > 0"
                        class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-3"
                    >
                        <div v-for="p in photos.filter((p) => p.type !== 'pdf')" :key="p.id" class="col-span-1">
                            <img :src="'/api/photos/show/' + p.file"/>
                            <dd class="pt-1 text-sm leading-5 text-slate-600">
                                {{ forestreeHelpers.dateTime(p.created_at, true) }}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>

            <div v-if="apikey" class="pt-6 pb-12">
                <div class="px-4">
                    <div class="pb-8 sm:flex sm:items-center">
                        <div class="sm:flex-auto">
                            <h1 class="text-xl font-semibold text-slate-900">Photos</h1>
                        </div>
                    </div>

                    <dl
                        v-if="photos && photos.length > 0"
                        class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-3"
                    >
                        <div v-for="p in photos.filter((p) => p.type !== 'pdf')" :key="p.id" class="col-span-1">
                            <img :src="'/public/photos/show/' + p.file"/>
                            <dd class="pt-1 text-sm leading-5 text-slate-600">
                                {{ forestreeHelpers.dateTime(p.created_at, true) }}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>
        </div>
    </div>
</template>
